import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { BadgeCell, ItemListCell, TextButtonCell } from '~/components/common';

import { maskId, maskName } from '~/utils/common';

import { OUTBOUND_STATUS_BADGE_COLOR } from '~/constants/order';

import type { TypeOrders } from '~/types/order.types';

/**
 * [출고주문관리 > 출고주문 조회] 페이지 테이블
 *
 * @note 행 선택(다중)은 3PL 권한일 때만 No 칸 체크박스로 한다.
 */
const getOrderOrdersColumnDefs = ({
  hasPermission3PL,
  onClickOutboundCode,
}: {
  hasPermission3PL: boolean;
  onClickOutboundCode: (outboundCode: string) => Promise<void>;
}) => {
  const columnDefs: TypeTableColumn<TypeOrders>[] = [
    {
      id: 'no',
      header: 'No',
      cell: ({ row }) => row.index + 1,
      size: 72,
      meta: { selection: hasPermission3PL },
    },
    {
      id: 'outbound.outboundStatus',
      accessorFn: (data) => data.outbound?.outboundStatus,
      header: '출고상태',
      size: 82,
      minSize: 82,
      cell: ({ getValue }) => {
        const value = getValue<TypeOrders['outbound']['outboundStatus'] | undefined>();
        if (!value?.code) return '-';
        return (
          <BadgeCell
            value={value.code}
            colorMap={{ [value.code]: { label: value.name, color: OUTBOUND_STATUS_BADGE_COLOR[value.code] } }}
            variant="light"
            status
          />
        );
      },
    },
    {
      id: 'order.clientOrderCode',
      accessorFn: (data) => data.order?.clientOrderCode,
      header: '판매처 주문번호',
      size: 160,
    },
    {
      id: 'outbound.outboundCode',
      accessorFn: (data) => data.outbound?.outboundCode,
      header: '출고요청번호',
      size: 164,
      minSize: 164,
      cell: ({ getValue }) => {
        const outboundCode = getValue<string | undefined>();
        if (!outboundCode) return '-';
        return (
          <TextButtonCell showDashIfNoValue={false} onClick={() => onClickOutboundCode(outboundCode)}>
            {outboundCode}
          </TextButtonCell>
        );
      },
    },
    {
      id: 'delivery.deliveryDate',
      accessorFn: (data) => data.delivery?.deliveryDate,
      header: '배송예정일',
      size: 100,
      cell: ({ getValue }) => {
        const value = getValue<string | undefined>();
        return value ? value.replaceAll('-', '.') : '-';
      },
    },
    {
      id: 'delivery.operation.name',
      accessorFn: (data) => data.delivery?.operation?.name,
      header: '배송유형',
      size: 80,
    },
    {
      id: 'delivery.courier.name',
      accessorFn: (data) => data.delivery?.courier?.name,
      header: '배송사',
      size: 88,
    },
    {
      id: 'outbound.clusterCenter.name',
      accessorFn: (data) => data.outbound?.clusterCenter?.name,
      header: '생산센터',
      size: 80,
    },
    {
      id: 'order.orderStoreName',
      accessorFn: (data) => data.order?.orderStoreName,
      header: '판매처',
      size: 148,
    },
    {
      id: 'order.paymentAt',
      accessorFn: (data) => data.order?.paymentAt,
      header: '결제완료일시',
      size: 160,
      cell: ({ getValue }) => {
        const value = getValue<string | undefined>();
        return value ? value.replaceAll('-', '.') : '-';
      },
    },
    {
      id: 'receiver.name',
      accessorFn: (data) => data.receiver?.name,
      header: '수령자명',
      size: 100,
      cell: ({ getValue }) => maskName(getValue<string>()),
    },
    {
      id: 'orderer.id',
      accessorFn: (data) => data.orderer?.id,
      header: '주문자 ID',
      size: 140,
      cell: ({ getValue }) => maskId(getValue<string>()),
    },
    {
      id: 'order.orderItems',
      header: '상품명',
      minSize: 240,
      meta: { flex: true },
      cell: ({ row }) => <ItemListCell value={row.original.order?.orderItems} fieldName="itemName" />,
    },
  ];

  return { columnDefs };
};

export default getOrderOrdersColumnDefs;
